import styled from 'styled-components';
import React, {
  SetStateAction,
  ChangeEvent,
  MouseEvent,
  Dispatch,
} from 'react';

import { FormJoin } from '@shared/interfaces/form';
import { HazmatPermit } from '@shared/enums/form';

type RadioButtonProps = {
  id: string;
  label: string;
  maxWidth: string;
  formData: FormJoin;
  required?: boolean;
  value: HazmatPermit;
  setFormData: Dispatch<SetStateAction<FormJoin>>;
};

function RadioButton({
  formData,
  value,
  setFormData,
  id,
  required,
  maxWidth,
  label,
}: RadioButtonProps) {
  const onChange = (e: ChangeEvent<HTMLElement>) => {
    const target = e.target as HTMLInputElement;
    setFormData({ ...formData, [id]: target.value });
  };

  const onClick = (e: MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLLabelElement;
    setFormData({ ...formData, [id]: target.htmlFor });
  };

  return (
    <Wrapper $maxWidth={maxWidth}>
      <LabelWrapper>
        <Title>{label}</Title>
        {required && <Asterics>*</Asterics>}
      </LabelWrapper>

      <RadioButtonWrapper>
        <RadioInputWrapper>
          <StyledInput
            type="radio"
            id={HazmatPermit.Yes}
            onChange={onChange}
            value={HazmatPermit.Yes}
            checked={value === HazmatPermit.Yes}
          />

          <Label htmlFor={HazmatPermit.Yes} onClick={onClick}>
            {HazmatPermit.Yes}
          </Label>
        </RadioInputWrapper>

        <RadioInputWrapper>
          <StyledInput
            type="radio"
            id={HazmatPermit.No}
            onChange={onChange}
            value={HazmatPermit.No}
            checked={value === HazmatPermit.No}
          />

          <Label htmlFor={HazmatPermit.No} onClick={onClick}>
            {HazmatPermit.No}
          </Label>
        </RadioInputWrapper>
      </RadioButtonWrapper>
    </Wrapper>
  );
}

const LabelWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const Asterics = styled.span`
  position: relative;
  font-size: 20px;
  top: -6px;
  left: 2px;
`;

const Title = styled.span`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: 15px;
`;

const Label = styled.label`
  font-size: 16px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const Wrapper = styled.div<{ $maxWidth?: string }>`
  display: flex;
  align-items: flex-start;
  flex-direction: column;

  height: 82px;
  width: 100%;
  max-width: ${({ $maxWidth }) => ($maxWidth ? $maxWidth : '420px')};
  margin-bottom: 10px;
`;

const RadioInputWrapper = styled.div`
  margin-right: 20px;

  display: flex;
  justify-content: center;
  align-items: center;
`;

const StyledInput = styled.input`
  display: none;

  & + label {
    display: inline-flex;
    align-items: center;
    cursor: pointer;
    font-size: 16px;
    color: ${({ theme }) => theme.colors.textPrimary};

    &::before {
      content: '';
      display: inline-flex;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      margin-right: 10px;
      border: 1px solid ${({ theme }) => theme.colors.divider};
      background: ${({ theme }) => theme.colors.cardBackground};
      border-radius: 50%;
      position: relative;
      transition:
        background 0.2s ease,
        border-color 0.2s ease;
    }
  }

  &:checked + label::before {
    content: '✓';
    font-size: 17px;
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const RadioButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

export default RadioButton;
